import Phaser from 'phaser';
import { PED_BARKS, AGGRO_BARKS, type Bark } from '../config';
import type { LiverpoolMap } from '../world/LiverpoolMap';
import { ScouseSpeaker } from '../audio/ScouseSpeaker';

export class Pedestrian {
  readonly sprite: Phaser.Physics.Arcade.Sprite;
  hp = 30;
  fleeUntil = 0;
  private nextThinkAt = 0;
  private dir = new Phaser.Math.Vector2(0, 0);
  private bark?: Phaser.GameObjects.Text;
  private nextProxBarkAt = 0;
  private wasInRange = false;

  static readonly SKINS = ['ped_blue', 'ped_brown', 'ped_old', 'ped_woman', 'ped_survivor'];

  constructor(scene: Phaser.Scene, x: number, y: number, private map: LiverpoolMap) {
    const skin = Pedestrian.SKINS[Math.floor(Math.random() * Pedestrian.SKINS.length)];
    const s = scene.physics.add.sprite(x, y, skin);
    s.setScale(0.45);
    s.body!.setSize(16, 16);
    s.body!.setOffset((s.width - 16) / 2, (s.height - 16) / 2);
    s.setCollideWorldBounds(true);
    s.setData('ped', this);
    this.sprite = s;
  }

  damage(n: number, fromX: number, fromY: number) {
    this.hp -= n;
    const s = this.sprite;
    s.setTintFill(0xffffff);
    s.scene.time.delayedCall(80, () => s.clearTint());
    // flee away from source
    const dx = s.x - fromX, dy = s.y - fromY;
    const len = Math.hypot(dx, dy) || 1;
    this.dir.set(dx / len, dy / len);
    this.fleeUntil = s.scene.time.now + 4000;
    // Higher chance of an aggro shout after being hit
    if (this.hp > 0 && Math.random() < 0.85) {
      const ab = AGGRO_BARKS[Math.floor(Math.random() * AGGRO_BARKS.length)];
      this.say(ab);
    }
  }

  say(arg?: string | Bark) {
    if (this.bark) this.bark.destroy();
    const bark: Bark = typeof arg === 'string'
      ? { text: arg, say: arg }
      : arg ?? PED_BARKS[Math.floor(Math.random() * PED_BARKS.length)];
    const t = this.sprite.scene.add.text(this.sprite.x, this.sprite.y - 14, bark.text, {
      fontSize: '10px', color: '#fff', backgroundColor: 'rgba(0,0,0,0.6)', padding: { left: 3, right: 3 }
    }).setOrigin(0.5, 1).setDepth(50);
    this.bark = t;
    this.sprite.scene.time.delayedCall(1500, () => { t.destroy(); if (this.bark === t) this.bark = undefined; });
    ScouseSpeaker.speak(bark.say ?? bark.text);
  }

  update(time: number, playerX?: number, playerY?: number) {
    if (this.hp <= 0) { this.destroy(); return; }
    if (this.bark) this.bark.setPosition(this.sprite.x, this.sprite.y - 14);
    if (time >= this.nextThinkAt) {
      this.nextThinkAt = time + Phaser.Math.Between(700, 1800);
      if (time >= this.fleeUntil) {
        const ang = Math.random() * Math.PI * 2;
        this.dir.set(Math.cos(ang), Math.sin(ang));
      }
    }
    const speed = time < this.fleeUntil ? 110 : 50;
    this.sprite.setVelocity(this.dir.x * speed, this.dir.y * speed);
    if (this.dir.lengthSq() > 0.001) {
      this.sprite.rotation = Math.atan2(this.dir.x, -this.dir.y);
    }
    // Bounce off non-walkable
    const ahead = 12;
    if (!this.map.isWalkable(this.sprite.x + this.dir.x * ahead, this.sprite.y + this.dir.y * ahead)) {
      this.dir.scale(-1);
    }

    // Proximity bark: greet the player when they walk within ~70px.
    if (playerX != null && playerY != null) {
      const inRange = Phaser.Math.Distance.Between(this.sprite.x, this.sprite.y, playerX, playerY) < 70;
      if (inRange && !this.wasInRange && time >= this.nextProxBarkAt && Math.random() < 0.55) {
        this.say();
        this.nextProxBarkAt = time + 8000; // per-ped cooldown
      }
      this.wasInRange = inRange;
    }
  }

  get alive() { return this.hp > 0 && this.sprite.active; }

  destroy() {
    if (this.bark) this.bark.destroy();
    this.sprite.destroy();
  }
}
